// TablesList.jsx
import React from 'react';

const STATUSES = {
  occupied:  { icon: '🍽️',  bg: '#fff3f0', border: '#ffb09c', fg: '#8a2d1d' },
  vacant:    { icon: '🪑',  bg: '#f3fff3', border: '#9ce09c', fg: '#1d7a1d' },
  cleaning:  { icon: '🧼',  bg: '#f0f7ff', border: '#9cc7ff', fg: '#1d4f8a' },
  reserved:  { icon: '📌',  bg: '#fffaf0', border: '#ffd89c', fg: '#7a561d' },
  pending:   { icon: '⏳',  bg: '#f9f9ff', border: '#c1c1ff', fg: '#3d3d8a' },
  locked:    { icon: '🔒',  bg: '#f7f7f7', border: '#d0d0d0', fg: '#555' },
  default:   { icon: '🟢',  bg: '#f7f7f7', border: '#e3e3e3', fg: '#333' },
};

function isDisabled(visibility) {
  if (visibility === undefined || visibility === null) return false;
  const v = typeof visibility === 'string' ? visibility.trim().toLowerCase() : visibility;
  return v === 0 || v === '0' || v === false || v === 'false';
}

function getStatusMeta(table) {
  if (isDisabled(table.visibility)) {
    return { icon: '🚫', bg: '#f4f4f4', border: '#e0e0e0', fg: '#888', disabled: true };
  }
  const s = (table.status || table.orderstatus || '').toString().trim().toLowerCase();
  if (s.includes('occup')) return STATUSES.occupied;
  if (s.includes('vacan') || s === 'open' || s === 'free') return STATUSES.vacant;
  if (s.includes('clean')) return STATUSES.cleaning;
  if (s.includes('reserv')) return STATUSES.reserved;
  if (s.includes('pend')) return STATUSES.pending;
  if (table.lockBill === '1' || s.includes('lock')) return STATUSES.locked;
  return STATUSES.default;
}

const Chip = ({ children, muted }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 6px',
      fontSize: 12,
      borderRadius: 999,
      background: muted ? '#f0f0f0' : '#fff',
      border: '1px solid #ddd',
      marginRight: 6,
      whiteSpace: 'nowrap',
      color: muted ? '#777' : undefined,
    }}
  >
    {children}
  </span>
);

const Row = ({ active, onClick, icon, meta, children, subtitle }) => {
  const disabled = meta.disabled === true;

  return (
    <div
      onClick={onClick} // keep editable even when disabled
      style={{
        padding: '10px 12px',
        marginBottom: 8,
        cursor: 'pointer',
        backgroundColor: disabled ? meta.bg : (active ? meta.bg : '#fff'),
        borderRadius: 10,
        border: `1px solid ${disabled ? meta.border : (active ? meta.border : '#e6e6e6')}`,
        boxShadow: active && !disabled ? '0 1px 0 rgba(0,0,0,0.03)' : 'none',
        transition: 'background 120ms ease, border 120ms ease',
        opacity: disabled ? 0.9 : 1,
        filter: disabled ? 'grayscale(0.25)' : 'none',
      }}
      title={disabled ? 'Table disabled' : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: meta.fg, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {children}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
            {subtitle}
            {disabled && <Chip muted>Table disabled</Chip>}
          </div>
        </div>
      </div>
    </div>
  );
};

const TablesList = ({ tables, selectedTableId, onSelect }) => {
  return (
    <div style={{ maxHeight: '75vh',
        overflowY: 'auto'}}>
      {tables.map((table) => {
        const meta = getStatusMeta(table);
        const icon = meta.icon;

        const title = (
          <>
            {table.number ? `Table ${table.number}` : table.id}
            {' — '}
            {table.nickname ?? table.name ?? 'Table'}
          </>
        );

        const chips = (
          <>
            <Chip>{table.status || '—'}</Chip>
            {table.orderstatus ? <Chip>Action: {table.action}</Chip> : null}
            {table.occupants ? <Chip>👥 {table.occupants} / {table.capacity ?? table.occupants}</Chip> : null}
            {table.notification ? <Chip>🔔 {table.notification}</Chip> : null}
          </>
        );

        return (
          <Row
            key={table.id}
            active={selectedTableId === table.id}
            onClick={() => onSelect(table)}
            icon={icon}
            meta={meta}
            subtitle={chips}
          >
            {title}
          </Row>
        );
      })}
    </div>
  );
};

export default TablesList;
